package com.corbettcode.allaboutrocks.pages

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.toSitePalette
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 400.px

private val PRESS_IMAGE_MAX_HEIGHT = 800.px
private val PRESS_IMAGE_MAX_WIDTH = 900.px
private val PRESS_IMAGE_SMALLER_MAX_WIDTH = 600.px
private val PRESS_IMAGE_VERTICAL_MARGIN = 24.px

private val PROGRAM_POSTER_IMAGE_MAX_HEIGHT = 800.px
private val PROGRAM_POSTER_IMAGE_MAX_WIDTH = 400.px
private val PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN = 24.px

@InitRoute
fun initLearnBooks(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Books About Rocks"))
}

val LearnBookTitleStyle = CssStyle {
    base { Modifier.fontSize(1.0.cssRem) }          // mobile
    Breakpoint.MD { Modifier.fontSize(1.8.cssRem) } // desktop
}

val LearnBookRatingStyle = CssStyle {
    base { Modifier.fontSize(1.0.cssRem) }          // mobile
    Breakpoint.MD { Modifier.fontSize(0.9.cssRem) } // desktop
}

@Composable
private fun LearnBooksHeadline() {
    val sitePalette = ColorMode.current.toSitePalette()

    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books About Rocks"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun LearnBooksHero() {

    LearnBooksHeadline()

    // 1 column on mobile, 2 columns on md+ (text left, image right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/learn/books/books_poster.png"
                    , alt = "how to identify rocks"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Every rock has a story. By reading about geology, minerals, and the forces that shape our planet, we learn to see those stories hidden in stone.")
            }
            P {
                Text("A book can take us from ancient volcanic eruptions to quiet seas where layers of sediment slowly accumulated, or deep beneath the Earth where heat and pressure transformed ordinary rock into something new. With each page, familiar stones become clues to a much larger history.")
            }
            P {
                Text("Collecting rocks is more than gathering interesting pieces of stone—it is an invitation to explore, observe, and understand the remarkable processes that have shaped our world over millions and even billions of years.")
            }
        }
    }
}

@Composable
private fun Learn_BooksSection() {
    val sitePalette = ColorMode.current.toSitePalette()

    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books on how to Identify Rocks"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )
        }

        CarouselOfSections(
            items = learnBooksCarouselSlides
            , frameDuration = 8.seconds
        )
    }
}

@Page("learn_books")
@Layout(".components.layouts.PageLayout")
@Composable
fun LearnBooksPage() {
    LearnBooksHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    Learn_BooksSection()
}

val learnBooksCarouselSlides = listOf<@Composable () -> Unit>(
    {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/Rocks_and_Minerals_of_the_Pacific_Coast.jpg"
                , alt = "Rocks and Minerals of the Pacific Coast"
                , modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            P {
                Text("Get the perfect guide to rocks and minerals of the Pacific Coast Keep this tabbed booklet close at hand on your next rock-hunting adventure. Based on Dan R. Lynch and Bob Lynch's best-selling field guides and featuring 52 of the most common and desirable rocks and minerals along the coasts of California, Oregon, and Washington, the booklet is organized by rocks/minerals and then by general appearance for quick and easy identification. Narrow your choices by appearance, and view photos of just a few specimens at a time. The easy-to-use format means you'll quickly find what you need to know. Plus, the quick guide is much easier to use than laminated foldouts, and the tear-resistant pages help to make the book durable in the field.")
            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/washington_oregon_rocks_and_minerals.jpg"
                , alt = "Washington-Oregon rocks and minerals"
                , modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            P {
                Text("Identify and collect rocks and minerals with the perfect guide to the Pacific Northwest! With this famous field guide by Dan R. Lynch and Bob Lynch, field identification is simple and informative. The book features comprehensive entries for 124 rocks and minerals, from common rocks to rare finds. That means you’re more likely to identify what you’ve found. The authors know rocks and took their own full-color photographs to depict the detail needed for identification―no more guessing from line drawings. The field guide’s easy-to-use format helps you to quickly find what you need to know and where to look.")
            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agates_and_other_collectibles_of_the_pacific_coast.jpg"
                , alt = "how to tumble rocks"
                , modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            P {
                Text("Keep this tabbed booklet close at hand on your next rock-hunting adventure. Based on Dan R. Lynch’s best-selling field guides and featuring the most common and desirable agates and other collectibles along the Pacific Coast, the booklet is organized by rocks/minerals and then by general appearance for quick and easy identification. With six varieties of agates, as well as popular finds like Jasper, Quartz, and even Jade, this handy guide has the information you want. Narrow your choices by appearance, and view photos of just a few specimens at a time. The easy-to-use format means you’ll quickly find what you need to know. Plus, the quick guide is much easier to use than laminated foldouts, and the tear-resistant pages help to make the book durable in the field.")
            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agatesjasper.webp"
                , alt = "agates jasper"
                , modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column() {
                SpanText(
                    "Collecting Agates and Jaspers of North America"
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
                SpanText(
                    "4.3 on Goodreads"
                    , LearnBookRatingStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )

                P {
                    Text("If you have any interest whatsoever in agates and jasper and if you intend to do some traveling, then this is a good book to have. The author does an impressive job showcasing the material that can be found in most of the US states, as well as parts of Canada and Mexico. I like that the author didn’t use museum grade specimens for her examples, which seems rare in the world of rock books. Instead, she uses good, but still obtainable, rock samples. My only real criticism of the book is that the author lists a price/value of the specimens which was likely inaccurate, even at the time of printing, since the market value of rocks can change rapidly.")
                }
            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agate1.webp"
                , alt = "agates treasures"
                , modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column() {
                SpanText(
                    "Agates: Treasures of the Earth"
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
                SpanText(
                    "4.0 on Goodreads"
                    , LearnBookRatingStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )

                P {
                    Text("This book provides a very well-rounded overview of the subject of Agates in a way that I haven’t seen done anywhere else. From the formation of agates to how they have been used throughout history, the book covers it all and would make an excellent addition to someone’s library or as a gift for someone who loves agates. It walks to the line of being technical but doesn’t cross it, which makes the material very digestible for the amateur collector.")
                }
            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/Metamorphicrocks.webp"
                , alt = "agates treasures"
                , modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            P {
                Text("This is such an incredible book on the subject! I have struggled with the identification of complex metamorphic rocks for a while now, but after reading through this book, I feel like it’s now a subject that I have a much better understanding of. The book has hundreds of high-quality photos and is written in a way that even the amateur can understand.")
            }
        }
    }
)
